import { getAgentRunAppAgentRunsRunIdGetOptions } from '@/client/@tanstack/react-query.gen'
import type { AgentRunDetail } from '@/client/types.gen'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useMemo } from 'react'
import { AgentRunDetailSkeleton } from './-components/run-detail-skeleton'
import { GenericAgentDetail } from './-agents/generic-agent-detail'
import { ToolGraphDetail } from './-agents/tool-graph/tool-graph-detail'
import type { AgentDetailViewProps } from './-shared/agent-detail-types'
import { useLiveRunEvents } from './-shared/use-run-events'

export const Route = createFileRoute('/_authed/_user/agent-runs/$runId')({
  beforeLoad: ({ context }) => {
    if (!context.auth.canUseAgentRuns) {
      throw redirect({ to: '/' })
    }
    return { getTitle: () => 'Agent Run' }
  },
  component: AgentRunDetailPage,
})

const RUN_ID_PATTERN = /^\d+$/
const TERMINAL_STATUSES = new Set<AgentRunDetail['status']>(['COMPLETED', 'FAILED'])

const AGENT_VIEWS: Record<string, (props: AgentDetailViewProps) => React.ReactNode> = {
  ui_agent: ToolGraphDetail,
  enrichment_agent: ToolGraphDetail,
  contact_agent: ToolGraphDetail,
  demo_1: ToolGraphDetail,
  demo_2: ToolGraphDetail,
  demo_3: ToolGraphDetail,
}

function AgentRunDetailPage() {
  const { runId } = Route.useParams()
  const isValid = RUN_ID_PATTERN.test(runId)

  const { data: run, isLoading, isError, error } = useQuery({
    ...getAgentRunAppAgentRunsRunIdGetOptions({ path: { run_id: Number(runId) } }),
    enabled: isValid,
  })

  const isRunning = run ? !TERMINAL_STATUSES.has(run.status) : false
  const liveEvents = useLiveRunEvents(isValid ? Number(runId) : undefined, isValid && isRunning)

  const events = useMemo(() => {
    const persisted = run?.events ?? []
    const seen = new Set(persisted.map(e => e.seq))
    const merged = [...persisted]
    for (const ev of liveEvents) {
      if (!seen.has(ev.seq)) {
        merged.push(ev)
        seen.add(ev.seq)
      }
    }
    return merged.toSorted((a, b) => a.seq - b.seq)
  }, [run?.events, liveEvents])

  if (!isValid) {
    return (
      <div className="px-4 py-4 lg:px-6 md:py-6">
        <Link to="/agent-runs">
          <Button variant="ghost" size="sm" className="gap-2 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </Link>
        <div className="text-muted-foreground">Invalid run id.</div>
      </div>
    )
  }

  const AgentView = run ? (AGENT_VIEWS[run.agentName] ?? GenericAgentDetail) : undefined

  return (
    <div className="flex flex-col gap-4 px-4 py-4 lg:px-6 md:py-6">
      <Link to="/agent-runs">
        <Button variant="ghost" size="sm" className="gap-2 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          All runs
        </Button>
      </Link>

      {isLoading && <AgentRunDetailSkeleton />}

      {isError && (
        <Card>
          <CardContent className="py-10 text-center text-red-600">
            Failed to load run:
            {' '}
            {String(error)}
          </CardContent>
        </Card>
      )}

      {run && AgentView && <AgentView run={run} events={events} isRunning={isRunning} />}
    </div>
  )
}
